import api from './api';

export interface Webhook {
  id: string;
  company_id: string;
  name: string;
  url: string;
  secret?: string;
  event_types: string[];
  description?: string;
  is_active: boolean;
  payload_version?: 'v1' | 'v2';
  disabled_at?: string | null;
  disabled_reason?: string | null;
  created_at: string;
  updated_at: string;
  stats?: {
    total_deliveries: number;
    successful: number;
    failed: number;
    pending: number;
    avg_duration_ms: number | null;
    last_delivery_at: string | null;
  };
}

export interface WebhookDelivery {
  id: string;
  webhook_id: string;
  event_id?: string | null;
  event_type?: string;
  event_category?: string;
  device_id?: string;
  request_url?: string;
  request_payload?: any;
  request_headers?: Record<string, any>;
  status: 'success' | 'failed' | 'pending' | 'cancelled';
  http_status?: number;
  response_body?: string;
  error_message?: string;
  duration_ms?: number;
  attempts?: number;
  next_retry_at?: string | null;
  delivered_at?: string | null;
  failed_at?: string | null;
  idempotency_key?: string;
  created_at: string;
}

export interface TestWebhookResponse {
  success: boolean;
  http_status?: number;
  response_body?: string;
  error_message?: string;
  duration_ms?: number;
}

export interface CreateWebhookData {
  name: string;
  url: string;
  event_types: string[];
  description?: string;
  // Delivery payload format. The app creates new webhooks as 'v2' (the
  // current contract); 'v1' is the frozen legacy envelope.
  payload_version?: 'v1' | 'v2';
}

export interface UpdateWebhookData {
  name?: string;
  url?: string;
  event_types?: string[];
  description?: string;
  is_active?: boolean;
  payload_version?: 'v1' | 'v2';
}

export interface DeliveryListParams {
  limit?: number;
  offset?: number;
  status?: string;
}

export interface DeliveryListResponse {
  deliveries: WebhookDelivery[];
}

export async function getWebhooks(companyId?: string): Promise<Webhook[]> {
  const response = await api.get<{ webhooks: Webhook[] }>('/webhooks', {
    params: companyId ? { company_id: companyId } : {},
  });
  return response.data.webhooks;
}

export async function createWebhook(data: CreateWebhookData, companyId?: string): Promise<Webhook> {
  const body = companyId ? { ...data, company_id: companyId } : data;
  const response = await api.post<{ webhook: Webhook }>('/webhooks', body);
  return response.data.webhook;
}

export async function updateWebhook(id: string, data: UpdateWebhookData): Promise<Webhook> {
  const response = await api.put<{ webhook: Webhook }>(`/webhooks/${id}`, data);
  return response.data.webhook;
}

export async function deleteWebhook(id: string): Promise<void> {
  await api.delete(`/webhooks/${id}`);
}

export async function testWebhook(id: string): Promise<TestWebhookResponse> {
  const response = await api.post<TestWebhookResponse>(`/webhooks/${id}/test`);
  return response.data;
}

export async function getWebhookDeliveries(id: string, params: DeliveryListParams = {}): Promise<DeliveryListResponse> {
  const response = await api.get<DeliveryListResponse>(`/webhooks/${id}/deliveries`, { params });
  return response.data;
}

export async function regenerateWebhookSecret(id: string): Promise<{ secret: string }> {
  const response = await api.post<{ secret: string }>(`/webhooks/${id}/regenerate-secret`);
  return response.data;
}
